// Types for the ADMS (push) biometric admin surface — /api/biometric/v2.

export type CommMode = 'push_adms' | 'lan_bridge' | 'direct_tcp';

export interface BiometricDevice {
  _id: string;
  serial_number: string;
  name?: string;
  device_name?: string;
  location?: string;
  comm_mode?: CommMode;
  /** Also read over the office network (needs ip_address), as well as receiving pushes. */
  lan_poll?: boolean;
  port?: number;
  /** The machine called in by itself and nobody has approved it yet. */
  pending_approval?: boolean;
  last_sync_at?: string | null;
  last_sync_status?: string;
  last_sync_message?: string;
  last_lan_poll_error?: string;
  is_active: boolean;
  online: boolean;
  firmware_version?: string;
  platform?: string;
  device_model?: string;
  push_version?: string;
  ip_address?: string;
  user_count?: number;
  fp_count?: number;
  face_count?: number;
  palm_count?: number;
  transaction_count?: number;
  total_punches_received?: number;
  last_heartbeat_at?: string | null;
  last_punch_at?: string | null;
  pending_commands?: number;
  auth_key?: string;
  timezone_offset?: string;
  delay_seconds?: number;
  error_delay?: number;
  realtime?: boolean;
  ack_style?: 'OK: n' | 'OK';
  door_count?: number;
}

export type CommandStatus = 'pending' | 'sent' | 'acked' | 'failed' | 'expired';

export interface BiometricCommand {
  _id: string;
  device_sn: string;
  cmd_id: number;
  command: string;
  kind?: string;
  status: CommandStatus;
  return_code?: number | null;
  return_message?: string;
  attempts?: number;
  created_at: string;
  sent_at?: string | null;
  acked_at?: string | null;
}

export interface BiometricEmployee {
  id: string;
  display_name: string;
  department?: string;
  role?: string;
  biometric_id: string;
  template_counts: Record<string, number>;
  template_total: number;
  last_punch_at?: string | null;
}

export interface UnmappedPin {
  biometric_id: string;
  punch_count: number;
  last_punch_at?: string | null;
  device_name?: string;
}

export interface BiometricPunch {
  _id: string;
  biometric_id: string;
  device_sn?: string;
  device_code?: string;
  punch_time: string;
  punch_state?: number;
  verification_mode?: number;
  att_photo_path?: string;
  source?: string;
  user_id?: { display_name?: string; department?: string } | null;
}

export type AttendanceStatus = 'P' | 'A' | 'HD' | 'WO' | 'HO' | 'L' | 'LWP' | 'OD' | 'MP';

export interface AttendanceDay {
  _id: string;
  user_id?: { _id?: string; display_name?: string; department?: string } | null;
  biometric_id?: string;
  log_date: string;
  shift_name?: string;
  first_in?: string | null;
  last_out?: string | null;
  punch_count?: number;
  worked_minutes?: number;
  late_minutes?: number;
  early_out_minutes?: number;
  ot_minutes?: number;
  status: AttendanceStatus;
  is_manual_override?: boolean;
  override_reason?: string;
  locked?: boolean;
}

export interface BiometricShift {
  _id: string;
  name: string;
  code?: string;
  start_time: string;
  end_time: string;
  crosses_midnight?: boolean;
  grace_in_minutes?: number;
  grace_out_minutes?: number;
  half_day_after_minutes?: number;
  absent_after_minutes?: number;
  min_hours_full_day?: number;
  min_hours_half_day?: number;
  break_minutes?: number;
  break_auto_deduct?: boolean;
  ot_starts_after_minutes?: number;
  ot_rounding_minutes?: number;
  ot_max_hours_per_day?: number;
  ot_multiplier?: number;
  weekly_offs?: number[];
  is_active?: boolean;
}

export interface BiometricRoster {
  _id: string;
  user_id?: { _id?: string; display_name?: string; department?: string } | null;
  shift_id?: { _id?: string; name?: string; start_time?: string; end_time?: string } | null;
  effective_from: string;
  effective_to?: string | null;
  department?: string;
}

export interface BiometricOpLog {
  _id: string;
  device_sn?: string;
  op_code: number;
  admin_pin?: string;
  target_pin?: string;
  op_time?: string | null;
  description: string;
  created_at: string;
}

export interface Regularisation {
  _id: string;
  user_id?: { display_name?: string; department?: string } | null;
  log_date: string;
  requested_in?: string;
  requested_out?: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  remarks?: string;
  created_at: string;
}

export interface DeviceHealth {
  serial_number: string;
  name?: string;
  is_active: boolean;
  online: boolean;
  last_heartbeat_at?: string | null;
  pending_commands: number;
  failed_commands: number;
  last_punch_at?: string | null;
  punch_lag_seconds?: number | null;
}

export interface ReportEnvelope {
  title: string;
  columns: string[];
  rows: (string | number)[][];
  meta?: Record<string, unknown>;
}

// ── Display helpers ──────────────────────────────────────────────────────────

export const PUNCH_STATES: Record<number, string> = {
  0: 'Check-In',
  1: 'Check-Out',
  2: 'Break-Out',
  3: 'Break-In',
  4: 'OT-In',
  5: 'OT-Out',
};

export const VERIFY_MODES: Record<number, string> = {
  0: 'Password/Auto',
  1: 'Fingerprint',
  2: 'Card+FP',
  3: 'Password',
  4: 'Card',
  5: 'FP+Password',
  15: 'Face',
  25: 'Palm',
};

export const STATUS_LABELS: Record<AttendanceStatus, string> = {
  P: 'Present',
  A: 'Absent',
  HD: 'Half Day',
  WO: 'Weekly Off',
  HO: 'Holiday',
  L: 'Leave',
  LWP: 'Leave (unpaid)',
  OD: 'On Duty',
  MP: 'Missing Punch',
};

/** Tailwind classes for muster-roll cells, keyed by status. */
export const STATUS_CELL: Record<AttendanceStatus, string> = {
  P: 'bg-success/15 text-success-text',
  A: 'bg-danger/15 text-danger-text',
  HD: 'bg-warning/15 text-warning-text',
  WO: 'bg-surface-variant text-on-surface-variant',
  HO: 'bg-info/15 text-info-text',
  L: 'bg-primary-container text-on-primary-container',
  LWP: 'bg-danger/10 text-danger-text',
  OD: 'bg-info/10 text-info-text',
  MP: 'bg-warning/25 text-warning-text',
};

export const REPORT_OPTIONS = [
  { value: 'daily', label: 'Daily Attendance', param: 'date' as const },
  { value: 'monthly', label: 'Muster Roll (monthly)', param: 'month' as const },
  { value: 'late-early', label: 'Late Arrivals & Early Exits', param: 'range' as const },
  { value: 'overtime', label: 'Overtime', param: 'range' as const },
  { value: 'absentee', label: 'Absentees', param: 'range' as const },
  { value: 'first-in-last-out', label: 'First In / Last Out', param: 'range' as const },
  { value: 'device-wise', label: 'Device-wise Punches', param: 'range' as const },
  { value: 'payroll-export', label: 'Payroll Export', param: 'month' as const },
  { value: 'access-log', label: 'Access Log', param: 'range' as const },
];

export function minutesToHhMm(minutes?: number): string {
  const m = Math.max(0, Math.round(minutes ?? 0));
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

export function istTime(value?: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleTimeString('en-GB', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

export function istDateTime(value?: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: false });
}

/** "3m ago" style relative label for heartbeats. */
export function relativeTime(value?: string | null): string {
  if (!value) return 'never';
  const seconds = Math.round((Date.now() - new Date(value).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}
